import {useEffect, useMemo, useRef, useState} from "react";
import {AxiosError} from "axios";
import {toast} from "@/src/modules/core/hooks/use-toast";
import type {UseFormReturn} from "react-hook-form";

import {Sheet, SheetContent} from "@/components/ui/sheet";
import useHousehold from "@/src/modules/households/hooks/use-household";
import type {Household} from "@/src/modules/households/types";
import {getMeHouseholdId} from "@/src/modules/core/lib/get-me-household-id";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";
import {useCancelHelpTourOnSheetClose} from "@/src/modules/help/hooks/use-cancel-help-tour-on-sheet-close";

import {Loader} from "../../core/components";
import {
  getHouseholdAmenityRestrictionErrorMessage,
  getResidentHouseholdAmenityRestrictionErrorMessage,
  isHouseholdAmenityRestrictionError,
} from "@/src/modules/households/lib/is-household-amenity-restriction-error";
import {getVehicleHouseholdId} from "../../vehicles/lib/get-vehicle-household-id";
import {vehicleHouseHoldToHousehold} from "../../vehicles/lib/vehicle-house-hold-to-household";
import useAccountability from "../../vehicles/hooks/use-accountability";
import useVehicle from "../../vehicles/hooks/use-vehicle";
import type {Vehicle} from "../../vehicles/types";
import useCreateSafelisting from "../hooks/use-create-safelisting";
import useUpdateSafelisting from "../hooks/use-update-safelisting";
import {formValuesToCreateRequest, formValuesToUpdateRequest} from "../services/serializer";
import {SAFELISTING_HELP_TOURS} from "../lib/safelisting-help";
import {
  getSafelistingModificationBlockedTitle,
  isSafelistingModificationBlocked,
} from "../lib/safelisting-parent-status";
import {Safelisting, type SafelistingFormValues} from "../types";
import {SafelistingForm} from "./form";

const SAFELISTING_TYPE = "visitor";

function toSafelistingFormHousehold(
  household: Household | null | undefined,
): Pick<Household, "house_hold_id" | "name" | "amenity_access_restricted" | "restricted_amenities"> | null {
  if (household?.house_hold_id == null) return null;
  return {
    house_hold_id: household.house_hold_id,
    name: household.name,
    amenity_access_restricted: household.amenity_access_restricted,
    restricted_amenities: household.restricted_amenities,
  };
}

type SafelistingsSheetFormProps = {
  open: boolean;
  safelisting?: Safelisting | null;
  onClose: () => void;
  /** For create: vehicle context when opening from vehicle details or dispatch. */
  vehicle?: Vehicle | null;
};

export function SafelistingsSheetForm({
  open,
  safelisting,
  onClose,
  vehicle: vehicleFromProps,
}: SafelistingsSheetFormProps) {
  const me = useStoredMe()!;
  const userProperty = useStoredUserProperty()!;
  const isEdit = safelisting != null;
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  useEffect(() => {
    if (!open) setSelectedVehicle(null);
  }, [open]);

  const vehicleId =
    vehicleFromProps?.vehicle_id ??
    selectedVehicle?.vehicle_id ??
    safelisting?.vehicle?.vehicle_id ??
    null;

  const {isPending: isLoadingVehicle, vehicle: fetchedVehicle} = useVehicle(vehicleId);
  const {mutateAsync: mutateCreateSafelisting} = useCreateSafelisting();
  const {mutateAsync: mutateUpdateSafelisting} = useUpdateSafelisting();
  const sourceVehicle =
    vehicleFromProps ?? selectedVehicle ?? fetchedVehicle ?? safelisting?.vehicle ?? null;
  /** Nested `resident` or `resident_id` — API shapes vary; both imply a resident vehicle for locking. */
  const isResidentVehicleContext =
    Boolean(sourceVehicle?.resident) ||
    (sourceVehicle?.resident_id != null && sourceVehicle.resident_id !== 0);
  const residentVehicleHouseholdId = isResidentVehicleContext
    ? getVehicleHouseholdId(sourceVehicle)
    : null;
  const residentVehicleHousehold =
    vehicleHouseHoldToHousehold(sourceVehicle?.house_hold) ??
    (residentVehicleHouseholdId
      ? {
          house_hold_id: residentVehicleHouseholdId,
          name:
            sourceVehicle?.resident?.house_hold?.name?.trim() ||
            `Household #${residentVehicleHouseholdId}`,
        }
      : null);
  const isResidentVehicleHouseholdLocked = residentVehicleHousehold != null;
  const residentUserHouseholdId = me.isResident ? getMeHouseholdId(me) : null;
  const {household: residentUserHouseholdFromApi} = useHousehold(residentUserHouseholdId);
  const residentUserHousehold = useMemo(() => {
    if (residentUserHouseholdId == null) return null;
    if (residentUserHouseholdFromApi) {
      return toSafelistingFormHousehold(residentUserHouseholdFromApi);
    }
    const embedName = me.house_hold?.name?.trim();
    return {
      house_hold_id: residentUserHouseholdId,
      name: embedName || `Household #${residentUserHouseholdId}`,
    };
  }, [me.house_hold?.name, residentUserHouseholdFromApi, residentUserHouseholdId]);
  const lockedHousehold = residentUserHousehold ?? residentVehicleHousehold;
  const isHouseholdSelectionLocked =
    (me.isResident && residentUserHouseholdId != null) || isResidentVehicleHouseholdLocked;
  const defaultAccountabilityHouseholdId =
    residentUserHouseholdId ??
    residentVehicleHouseholdId ??
    safelisting?.household?.house_hold_id ??
    null;
  const [householdIdForAccountability, setHouseholdIdForAccountability] = useState<number | null>(
    defaultAccountabilityHouseholdId,
  );
  const {
    accountability,
    isError: isAccountabilityError,
    error: accountabilityError,
    errorUpdatedAt: accountabilityErrorUpdatedAt,
  } = useAccountability({
    vehicleId,
    householdId: householdIdForAccountability,
    enabled: open && vehicleId != null && !isLoadingVehicle,
  });

  const formRef = useRef<UseFormReturn<SafelistingFormValues> | null>(null);

  const handleInit = (form: UseFormReturn<SafelistingFormValues>) => {
    formRef.current = form;
  };

  const handleSubmit = async (values: SafelistingFormValues) => {
    if (vehicleId == null) {
      toast.error("Select a vehicle to continue.");
      return;
    }

    const context = {
      vehicle_id: vehicleId,
      context_property_id: userProperty.id,
      safelisting,
      type: SAFELISTING_TYPE,
      omitOverrideFields: me.isResident,
    };

    if (safelisting != null) {
      const request = formValuesToUpdateRequest(values, context);
      await mutateUpdateSafelisting({id: safelisting.safelisting_id, data: request});
      toast.success("Safelisting updated");
    } else {
      const request = formValuesToCreateRequest(values, context);
      try {
        await mutateCreateSafelisting(request);
      } catch (error) {
        if (isHouseholdAmenityRestrictionError(error)) {
          const message = me.isResident
            ? getResidentHouseholdAmenityRestrictionErrorMessage(error)
            : getHouseholdAmenityRestrictionErrorMessage(error);
          toast.error(message);
          return;
        }
        throw error;
      }
      toast.success("Safelisting created");
    }
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    setHouseholdIdForAccountability(defaultAccountabilityHouseholdId);
  }, [open, defaultAccountabilityHouseholdId, safelisting?.safelisting_id]);

  useEffect(() => {
    if (!open || safelisting == null) return;
    if (!isSafelistingModificationBlocked(safelisting)) return;
    toast.error(getSafelistingModificationBlockedTitle("edit"));
    onClose();
  }, [open, onClose, safelisting]);

  useEffect(() => {
    if (!isAccountabilityError) return;
    const status = (accountabilityError as AxiosError | null)?.response?.status;
    if (status === 422) {
      toast.error("We could not validate accountability for this household.");
      return;
    }
    if (status === 403) {
      toast.error("You do not have permission to view accountability for this vehicle.");
      return;
    }
    if (status === 404) {
      toast.error("Accountability data was not found for the selected vehicle and household.");
      return;
    }
    toast.error("Failed to load accountability data.");
  }, [isAccountabilityError, accountabilityError, accountabilityErrorUpdatedAt]);

  /** When `accountability` is null we must not infer limits from defaults. */
  const safelistingsAccountability = accountability?.safelistings.accountability ?? null;

  useCancelHelpTourOnSheetClose(open);

  return (
    <Sheet
      open={open}
      askConfirmation={() => {
        if (!formRef.current) return false;
        return Object.keys(formRef.current.formState.dirtyFields).length > 0;
      }}
      onOpenChange={onClose}
    >
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHelpHeader
          title={isEdit ? "Edit safelisting" : "Create safelisting"}
          createTourId={SAFELISTING_HELP_TOURS.formCreate}
          showCreateTour={!isEdit}
        />
        <SheetFormScrollBody className="@container/safelisting-sheet">
          {isEdit && isLoadingVehicle ? (
            <Loader className="h-full min-h-48" />
          ) : (
            <SafelistingForm
              safelisting={safelisting}
              accountability={safelistingsAccountability}
              initialHousehold={lockedHousehold}
              lockHouseholdSelection={isHouseholdSelectionLocked}
              lockedHouseholdDisplay={residentUserHouseholdFromApi ?? null}
              onHouseholdChange={setHouseholdIdForAccountability}
              contextVehicle={sourceVehicle}
              onContextVehicleChange={
                !isEdit && me.isResident && vehicleFromProps == null
                  ? setSelectedVehicle
                  : undefined
              }
              hideHouseholdField={!isEdit && me.isResident && vehicleFromProps == null}
              onSubmit={handleSubmit}
              onInit={handleInit}
            />
          )}
        </SheetFormScrollBody>
      </SheetContent>
    </Sheet>
  );
}
